import { ChangeDetectorRef, Component, forwardRef, inject, Input, OnChanges, OnInit, Provider, SimpleChanges } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isEqual } from 'lodash';
import {
  MbsSize,
  ModalService,
  ModalSettings,
  PrependTopControlsLabel,
  SharedPersistentStateEnum,
  TableCellDirective,
  TableHeader
} from 'mbs-ui-kit';
import { TreeInModalComponent } from 'mbs-ui-shared@components/tree-in-modal/tree-in-modal.component';
import { TreeBunchesIcons } from 'mbs-ui-shared@models/backup/storages-type';
import { AgentType, minimalWindowsVersionForNewAsync } from 'mbs-ui-shared@models/Computer';
import { noop, Observable, of, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SelectHostStepValue } from 'wizards/models/select-host-models';
import { SelectVirtualDisksStepValue, VirtualDisk, VirtualDisksSelectedType } from 'wizards/models/select-virtual-disks-models';
import {
  SelectVirtualMachinesStepValue,
  VirtualMachine,
  VirtualMachinesSelectedType,
  VirtualMachinesType
} from 'wizards/models/select-virtual-machines-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import {
  MachinesDisksLoadedStatuses,
  ParamsForGetVirtualMachinesOrDisksList,
  WizardStepsService
} from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

import { AlertComponent, ButtonComponent, FormGroupComponent, RadioComponent, TableGridComponent, TagComponent } from 'mbs-ui-kit';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

const SelectVirtualDisksStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => SelectVirtualDisksStepComponent),
  multi: true
};

type ExtendedVirtualMachine = VirtualMachine & { existDisk?: boolean; disks?: VirtualDisk[] };

export class DisksHash {
  private storage: { [key: string]: VirtualDisk } = {};

  constructor() {
    return new Proxy(this, {
      set(target: DisksHash, key: string, newValue: VirtualDisk): boolean {
        target.storage[key.toLowerCase()] = newValue;

        return true;
      }
    });
  }

  hasDisk(uiGuid: string): boolean {
    return !!this.storage[uiGuid.toLowerCase()];
  }

  reset(): void {
    Object.keys(this.storage).forEach((key) => delete this.storage[key]);
  }
}

/**
 * Component mbs-select-virtual-disks-step
 * Displays the drives for the computers selected in the mbs-select-virtual-machines-step step
 * Used within the HyperV and VMWare wizards
 * @author Roman.Sh
 */
@UntilDestroy()
@Component({
  selector: 'mbs-select-virtual-disks-step',
  templateUrl: './select-virtual-disks-step.component.html',
  styleUrls: ['./select-virtual-disks-step.component.scss'],
  providers: [SelectVirtualDisksStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    RadioComponent,
    ReactiveFormsModule,
    TableGridComponent,
    TagComponent,
    NgbTooltipModule,
    TableCellDirective,
    PrependTopControlsLabel
  ]
})
export class SelectVirtualDisksStepComponent extends StepBase<SelectVirtualDisksStepValue> implements OnInit, OnChanges {
  mainService: RemoteManagementWizardsService;
  private cdr = inject(ChangeDetectorRef);
  private modalService = inject(ModalService);
  private i18nPipe = inject(I18NextPipe);
  private stepService = inject(WizardStepsService);

  @Input() selectedHostCredentials: SelectHostStepValue;
  @Input() selectedMachinesStepValue: SelectVirtualMachinesStepValue;
  public readonly elementsSelector = {
    name: {
      computerExcludeTag: 'computerExcludeTag',
      diskExcludeTag: 'diskExcludeTag',
      invalidTableAlert: 'invalidTableAlert'
    }
  };
  public readonly mbsSize = MbsSize;
  public readonly virtualDisksSelectedType = VirtualDisksSelectedType;
  public readonly viewMode = SharedPersistentStateEnum;
  private needUpdateTableSubject$: Subject<void> = new Subject();
  public virtualMachinesIcon = TreeBunchesIcons.HyperV;

  public validVersionForShowExcludes = false;
  public validVersionHyperVLabelDisk = false;
  public existNotLoadedMachines = false;

  public tableData: Array<ExtendedVirtualMachine> = [];

  public availableDisks: VirtualDisk[] = [];
  public selectedDisks: Array<ExtendedVirtualMachine & VirtualDisk> = [];
  public headers: TableHeader[] = [{ name: 'VM Name', gridColSize: '100fr', class: 'w-100', overflow: true }];
  public tableIsInvalid = false;
  public subHeaders: TableHeader[] = [
    { name: 'Disk Name', gridColSize: '64fr', overflow: true },
    { name: 'Size', overflow: true, gridColSize: '36fr', isGridColumn: true, class: '-right' }
  ];

  private VMHash: { [key: string]: VirtualMachine & { existDisk?: boolean; disks: VirtualDisk[] } } = {};
  private initialAllDisksHash: DisksHash = new DisksHash();

  private needOldAsync = true;
  private canUpdateSelectedDisks = true;
  private offlineFirst = true;
  private firstCallUpdateFormAfterBuildTable = true;
  private machinesDisksLoadedStatuses: MachinesDisksLoadedStatuses = null;
  private get shouldUseNewGetVirtualMachineDiskList(): boolean {
    const version = this.mainService.backupVersionUpdated?.substring(0, 2);

    return version && +version >= 80;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.subscribeToUpdateTableAndBaseData();
    if (this.isVMWare) this.virtualMachinesIcon = TreeBunchesIcons.VmWare;
    this.checkValidAgentVersion();
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      allDisks: new FormControl([]),
      disks: new FormControl([]),
      type: new FormControl(VirtualDisksSelectedType.Selected)
    });

    this.initFormEvents();

    this.mainService.machinesDisksStatuses$.pipe(untilDestroyed(this)).subscribe((statuses) => {
      this.machinesDisksLoadedStatuses = statuses;

      this.updateTableValidity();
    });
  }

  onStepFormChange(value: SelectVirtualDisksStepValue): void {
    this.value = {
      ...value,
      valid: this.stepForm.valid && (!!value.disks.length || value.type === VirtualDisksSelectedType.All) && !this.tableIsInvalid
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selectedMachinesStepValue) {
      const stepCurrent = changes?.selectedMachinesStepValue?.currentValue;
      const previousMachines = changes?.selectedMachinesStepValue?.previousValue?.machinesFullFormat;
      const currentMachines = stepCurrent?.machinesFullFormat;
      const isNotSelected = stepCurrent?.type !== VirtualMachinesSelectedType.Selected;
      const notEqualOrFirstIsOffline = !isEqual(previousMachines, currentMachines) || (this.isOffline && this.offlineFirst);

      if (isNotSelected || (currentMachines?.length && notEqualOrFirstIsOffline)) {
        if (this.isOffline) {
          this.waitFinishInitialSubjectAndRunAgain();
        } else {
          const mStatuses = this.mainService.machinesDisksStatuses$.value;

          if (mStatuses?.allVoid && !mStatuses.voidMachines?.length) this.subscribeToUpdateTableAndBaseData();

          this.needUpdateTableSubject$.next(undefined);
        }
      }
    }
  }

  waitFinishInitialSubjectAndRunAgain(): void {
    this.offlineFirst = false;
    setTimeout(() => this.needUpdateTableSubject$.next(undefined));
  }

  forceValid(): void {
    this.stepForm.updateValueAndValidity();
    this.stepForm.markAllAsTouched();
  }

  checkValidAgentVersion(): void {
    const version = this.mainService.backupVersionUpdated && this.mainService.backupVersionUpdated.substring(0, 3);
    this.validVersionForShowExcludes = version && +version >= (this.isHyperV ? 790 : 783);
    this.validVersionHyperVLabelDisk =
      this.isHyperV && this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated >= 790255;
    this.needOldAsync = !version || +version < minimalWindowsVersionForNewAsync;
  }

  buildVMHashesAndResetData(): void {
    this.canUpdateSelectedDisks = false;
    this.existNotLoadedMachines = false;
    this.VMHash = {};
    this.availableDisks = [];
    this.tableData = [];
    this.initialAllDisksHash = new DisksHash();

    if (this.isOffline) {
      this.value.disks.forEach((disk: VirtualDisk) => {
        this.VMHash[disk.virtualMachineId] = {
          ...{ virtualMachineId: disk.virtualMachineId, displayName: disk.virtualMachineName },
          existDisk: false,
          uiGuid: disk.virtualMachineId,
          disks: []
        } as VirtualMachine & { existDisk?: boolean; disks: [] };
      });

      return;
    }

    this.selectedMachinesStepValue.machinesFullFormat.forEach((machine: VirtualMachine) => {
      this.VMHash[machine.virtualMachineId] = { ...machine, uiGuid: machine.virtualMachineId, existDisk: false, disks: [] };
      if (machine.notLoaded) this.existNotLoadedMachines = true;
    });
  }

  subscribeToUpdateTableAndBaseData(): void {
    this.needUpdateTableSubject$
      .pipe(
        switchMap(() => {
          const params: Partial<SelectHostStepValue> = this.selectedHostCredentials || {};
          const isParamsValid = params.server && params.login && params.password;

          this.buildVMHashesAndResetData();
          this.mainService.machinesDisksStatuses$.next({ voidMachines: [], allVoid: false, loadingDisks: true });

          if (this.selectedMachinesStepValue.type !== VirtualMachinesSelectedType.Selected || this.isOffline) {
            return of(this.isOffline ? { data: this.value.disks } : {});
          }

          return this.getDisksAndBuildTableData(isParamsValid);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (data) => this.updateDataAfterLoadDisks(this.shouldUseNewGetVirtualMachineDiskList ? data?.data?.items : data?.data),
        error: () => this.mainService.machinesDisksStatuses$.next({ voidMachines: [], allVoid: true, loadingDisks: false })
      });
  }

  getDisksAndBuildTableData(isParamsValid: string): Observable<any> {
    const loadedMachines = [];

    if (this.isEdit) {
      this.selectedMachinesStepValue.machinesFullFormat.forEach((m) => {
        if (!this.VMHash[m.virtualMachineId].displayName) {
          this.VMHash[m.virtualMachineId].displayName = this.VMHash[m.virtualMachineId].name;
        }
        if (!this.VMHash[m.virtualMachineId].notLoaded) loadedMachines.push(m.virtualMachineId);
      });

      if (!loadedMachines?.length) {
        this.tableData = Object.values(this.VMHash);
        return of({});
      }
    }

    return isParamsValid || this.isHyperV
      ? this.stepService.getRemoteCommandData(this.getRequestParams(loadedMachines), this.mainService.hid, this.needOldAsync)
      : of({});
  }

  updateDataAfterLoadDisks(disks: VirtualDisk[]): void {
    if (disks?.length) {
      const disksExcludes = {};

      this.stepForm.get('allDisks').value.forEach((disk) => (this.initialAllDisksHash[disk.uiGuid] = disk));
      this.stepForm.get('disks')?.value?.forEach((d) => (disksExcludes[d.uiGuid] = d.excludes));

      disks.forEach((d) => {
        d.uiGuid = d.uuid + d.virtualMachineId;
        d.excludes = disksExcludes[d.uiGuid] || [];
      });

      const sortedData = disks.sort((a, b) => (a.virtualMachineName > b.virtualMachineName ? 1 : 0));
      this.stepForm.get('allDisks').reset(sortedData || []);

      sortedData.forEach((disk) => {
        disk.diskCapacityStr = this.getSizeFromDiskCapacity(disk.diskCapacity);

        if (this.VMHash[disk.virtualMachineId]) {
          this.VMHash[disk.virtualMachineId].existDisk = true;
          this.VMHash[disk.virtualMachineId].disks.push(disk);
          this.availableDisks.push(disk);
        }
      });

      this.tableData = Object.values(this.VMHash);
      this.allMachinesHasDisks();
      this.updateFormAfterBuildTable();
      this.canUpdateSelectedDisks = true;

      return;
    }

    this.mainService.machinesDisksStatuses$.next({ voidMachines: [], allVoid: true, loadingDisks: false });
  }

  allMachinesHasDisks(): void {
    const machinesDisksStatuses: MachinesDisksLoadedStatuses = { voidMachines: [], allVoid: false, loadingDisks: false };
    this.tableData.forEach((machine) => !machine.existDisk && machinesDisksStatuses.voidMachines.push(machine.virtualMachineId));
    this.mainService.machinesDisksStatuses$.next(machinesDisksStatuses);
  }

  getSizeFromDiskCapacity(capacity: number): string {
    const kb = +(capacity / 1024).toFixed(2);
    if (kb < 1024) {
      return kb ? Math.ceil(kb * 10) / 10 + ' KB' : '';
    }

    const mb = +(kb / 1024).toFixed(2);
    if (mb < 1024) {
      return mb ? Math.ceil(mb * 10) / 10 + ' MB' : '';
    }

    const gb = +(mb / 1024).toFixed(2);
    if (gb < 1024) {
      return gb ? Math.ceil(gb * 10) / 10 + ' GB' : '';
    }

    const tb = +(gb / 1024).toFixed(2);
    return tb ? Math.ceil(tb * 10) / 10 + ' TB' : '';
  }

  updateFormAfterBuildTable(): void {
    const control = this.stepForm.get('type');

    if (!this.isOffline) {
      const notDisks = !this.value?.disks?.length;
      const disksLengthEqualAvailable = this.value?.disks?.length === this.availableDisks?.length;
      const notExcludes = !this.value?.disks?.some((d) => !!d.excludes?.length);
      const notFirstAndAll = !this.firstCallUpdateFormAfterBuildTable && control.value === VirtualMachinesSelectedType.All;
      const notExcludeNotFirstAndAllOrDiffLength = notFirstAndAll && disksLengthEqualAvailable && notExcludes;

      control.reset(
        control.value === VirtualDisksSelectedType.Selected && (notDisks || notExcludeNotFirstAndAllOrDiffLength)
          ? VirtualDisksSelectedType.All
          : control.value
      );
    }

    this.virtualDiskSelectedTypeChangeHandler(control.value);

    if (this.firstCallUpdateFormAfterBuildTable) this.firstCallUpdateFormAfterBuildTable = false;
  }

  virtualDiskSelectedTypeChangeHandler(selectedType: VirtualDisksSelectedType): void {
    if (selectedType === VirtualDisksSelectedType.All) {
      this.selectedDisks = this.tableData.reduce((acc, next) => {
        acc.push(next, ...next.disks);

        return acc;
      }, []);

      this.stepForm.get('disks').reset(this.availableDisks);

      return void this.updateTableValidity();
    }

    if (this.existNotLoadedMachines) return;

    const machineIdArr = this.stepForm.get('disks')?.value.map((disk) => disk.virtualMachineId.toLowerCase());
    const diskUUIDArr = this.stepForm.get('disks')?.value.map((disk) => disk.uiGuid.toLowerCase());

    this.selectedDisks = this.tableData.reduce((acc, next) => {
      if (next.disks.every((d) => !this.initialAllDisksHash.hasDisk(d.uiGuid))) {
        acc.push(next, ...next.disks);

        return acc;
      }

      if (machineIdArr.includes(next.virtualMachineId.toLowerCase())) {
        const disks = next.disks.filter((d) => diskUUIDArr.includes(d.uiGuid.toLowerCase()) || !this.initialAllDisksHash.hasDisk(d.uiGuid));

        acc.push(next, ...disks);
      }

      return acc;
    }, []);

    this.stepForm.get('disks')?.reset(this.getDisksFromSelectedArray(this.selectedDisks));
    this.updateTableValidity();
  }

  diskSelectChangeHandler(selectedDisks: Array<ExtendedVirtualMachine & VirtualDisk>): void {
    if (this.canUpdateSelectedDisks && !isEqual(this.selectedDisks, selectedDisks)) {
      this.selectedDisks = selectedDisks;
      const newSelectedDisks: VirtualDisk[] = this.getDisksFromSelectedArray(selectedDisks);

      this.stepForm.reset({
        ...this.stepForm.value,
        disks: newSelectedDisks,
        type:
          newSelectedDisks.length < this.availableDisks.length || selectedDisks.some((disk) => !!disk.excludes?.length)
            ? VirtualDisksSelectedType.Selected
            : this.stepForm.value.type
      });
    }

    this.updateTableValidity();
    this.cdr.detectChanges();
  }

  excludeClickHandler(disk: VirtualDisk): void {
    if (this.isOffline) return;

    this.modalService
      .openCustom(TreeInModalComponent, this.getParamsForTreeModal(disk))
      .then((newExcludes: string[]) => this.updateExcludesAfterSelectInModal(disk, newExcludes))
      .catch(noop);
  }

  getParamsForTreeModal(disk: VirtualDisk): ModalSettings {
    const excludeParams = {
      title: this.i18nPipe.transform('wizards:exclude_items', { format: 'title' }),
      hid: this.mainService.hid,
      isVirtual: true,
      needOldAsync: this.needOldAsync,
      notCheckParentIfSelectAll: true,
      resultFolders: disk.excludes || [],
      params: {
        agentType: 'backup',
        commandType: 'GetVirtualMachineDiskContent',
        params: {
          VirtualMachineId: disk.virtualMachineId,
          DiskId: disk.id,
          path: null,
          offset: 0,
          limit: 120,
          Order: 'DisplayNameAsc',
          Search: null,
          Type: this.isVMWare ? VirtualMachinesType.VMWare : VirtualMachinesType.HyperV,
          Server: this.selectedHostCredentials?.server || '',
          Login: this.selectedHostCredentials?.login || '',
          Password: this.selectedHostCredentials?.password,
          PlanId: this.mainService.planId,
          IsCluster: false
        }
      },
      dataForPath: this.getValueForOperationsWithPath()
    };

    return { data: excludeParams, size: MbsSize.sm, collapsing: true };
  }

  updateExcludesAfterSelectInModal(disk: VirtualDisk, newExcludes: string[]): void {
    if (!newExcludes) return;

    const disks = this.stepForm.get('disks').value || [];
    const allDisks = this.stepForm.get('allDisks').value || [];
    const idx = disks.findIndex((d) => d.uuid === disk.uuid && d.id === disk.id && d.virtualMachineId === disk.virtualMachineId);
    const allIdx = allDisks.findIndex((d) => d.uuid === disk.uuid && d.id === disk.id && d.virtualMachineId === disk.virtualMachineId);
    const availableIdx = this.availableDisks.findIndex(
      (d) => d.uuid === disk.uuid && d.id === disk.id && d.virtualMachineId === disk.virtualMachineId
    );

    if (idx !== -1) disks[idx].excludes = newExcludes;
    if (allIdx !== -1) allDisks[allIdx].excludes = newExcludes;
    if (availableIdx !== -1) this.availableDisks[availableIdx].excludes = newExcludes;

    this.stepForm.get('disks').reset(disks || []);
    this.stepForm.get('allDisks').reset(allDisks || []);

    if (idx !== -1 && newExcludes.length) this.stepForm.get('type').reset(VirtualDisksSelectedType.Selected);
  }

  needShowTag(computer: ExtendedVirtualMachine): boolean {
    return this.stepForm?.get('type')?.value === VirtualDisksSelectedType.Selected && computer.disks.some((disk) => disk.excludes?.length);
  }

  private getRequestParams(loadedMachines: string[] = []): ParamsForGetVirtualMachinesOrDisksList {
    const idArray = loadedMachines?.length ? loadedMachines : Object.keys(this.VMHash);

    return {
      agentType: AgentType.Backup,
      commandType: this.shouldUseNewGetVirtualMachineDiskList ? 'GetVirtualMachineDiskList2' : 'GetVirtualMachineDiskList',
      params: {
        PlanId: this.mainService.planId,
        VirtualMachineIds: idArray,
        Type: this.isVMWare ? 'VMWare' : 'HyperV',
        Server: this.selectedHostCredentials?.server,
        Login: this.selectedHostCredentials?.login,
        Password: this.selectedHostCredentials?.password,
        IsCluster: false,
        SessionId: this.mainService.sessionId || null
      }
    };
  }

  private getDisksFromSelectedArray(selectedDisks: Array<ExtendedVirtualMachine & VirtualDisk>): VirtualDisk[] {
    const newSelectedDisks: VirtualDisk[] = [];

    selectedDisks.forEach((diskOrComp) => {
      if (!diskOrComp.disks) newSelectedDisks.push(diskOrComp);
    });

    return newSelectedDisks;
  }

  /**
   * There are 2 reasons to make table invalid:
   *  - One or more machines failed to load their disks
   *  - One or more machines has not selected disks
   *  @return {void}
   */
  private updateTableValidity(): void {
    const finish = (valid) => {
      this.tableIsInvalid = valid;

      queueMicrotask(() => {
        this.stepForm.updateValueAndValidity();
      });
    };

    const hasFailedDisks = this.machinesDisksLoadedStatuses?.allVoid || !!this.machinesDisksLoadedStatuses?.voidMachines.length;

    if (this.stepForm.get('type').value !== VirtualDisksSelectedType.Selected) {
      return void finish(hasFailedDisks);
    }

    if (hasFailedDisks) {
      return void finish(true);
    }

    const tableMachines: Array<string> = this.tableData.map((vm) => vm.virtualMachineId);
    const machinesFromSelectedDisks: Array<string> = this.stepForm.get('disks').value.map((disk) => disk.virtualMachineId);
    const hasEmptyMachine = tableMachines.some((vmId) => {
      const hasSelectedDisk = machinesFromSelectedDisks.includes(vmId);

      return !hasSelectedDisk;
    });

    finish(hasEmptyMachine);
  }
}
